import { useClient, useMutation } from '../../../../loco-sync';
import { ModelDataStore } from '@teamworksdev/loco-sync-client';
import { useUndoRedo } from '../../../utils/undoRedo';
import { AthleteDraftWorkoutDomain } from '@strength/domain';
import { M } from '@strength/loco-sync';
import { defaultLayer } from '@strength/db-schema';

type AthleteDraftWorkoutUndoRedoAction = AthleteDraftWorkoutDomain.Command;

export const useAthleteDraftWorkoutUndoRedo = (
  blockId: string,
  profileId: string,
) => {
  const mutation = useMutation();
  const store = useClient().getCache().getStore();

  const { undo, redo, addAction } = useUndoRedo<
    AthleteDraftWorkoutUndoRedoAction[]
  >({
    key: `${AthleteDraftWorkoutDomain.Module}:${blockId}-${profileId}`,
    getReverseAction: (commands) => getReverseCommands(commands, store),
    performAction: (commands) => {
      for (const command of commands) {
        mutation.mutate(command);
      }
    },
  });

  return {
    undo,
    redo,
    bulkMutate: (commands: readonly AthleteDraftWorkoutUndoRedoAction[]) => {
      addAction([...commands]);
      for (const command of commands) {
        mutation.mutate(command);
      }
    },
  };
};

function getReverseCommands(
  commands: AthleteDraftWorkoutUndoRedoAction[],
  store: ModelDataStore<M>,
): AthleteDraftWorkoutUndoRedoAction[] | null {
  const reverseCommands: AthleteDraftWorkoutUndoRedoAction[] = [];
  for (const command of commands) {
    const reverseCommand: AthleteDraftWorkoutUndoRedoAction = {
      module: command.module,
      identity: command.identity,
      edits: [],
    };

    for (const edit of command.edits) {
      // Ignore edits of "AthleteDraftWorkout" for purposes of undo/redo
      if (edit.action === 'create' || edit.action === 'delete') continue;

      const currentData = store.getOne(edit.modelName, {
        id: edit.modelId,
      }) ?? {
        // set some defaults here as a beginning state
        assignedDate: null,
        layer: defaultLayer(),
        name: null,
      };

      const reverseEdit: AthleteDraftWorkoutDomain.Edit | null = {
        modelName: edit.modelName,
        modelId: edit.modelId,
        action: 'update',
        data: {
          assignedDate: currentData.assignedDate,
          layer: currentData.layer,
          name: currentData.name,
        },
      };

      // Use unshift instead of push to reverse the order of edits
      reverseCommand.edits.unshift(reverseEdit);
    }

    // Use unshift instead of push to reverse the order of commands
    reverseCommands.unshift(reverseCommand);
  }

  return reverseCommands;
}
